import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import type { AuthErrorCode, AuthUser } from '@shared/api';

// Estado de sessão do cliente. A fonte da verdade continua sendo o cookie
// HttpOnly, que o JavaScript não lê — aqui só se guarda o que /api/auth/me
// respondeu, para a interface saber o que mostrar.

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  signup: (input: SignupInput) => Promise<AuthResult>;
  login: (email: string, password: string) => Promise<AuthResult>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export interface SignupInput {
  email: string;
  password: string;
  acceptedTerms: boolean;
}

export type AuthResult =
  | { ok: true }
  | { ok: false; error: AuthErrorCode | 'network'; retryAfter?: number };

const AuthContext = createContext<AuthState | null>(null);

async function post(path: string, body: unknown): Promise<AuthResult> {
  try {
    const response = await fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (response.ok) return { ok: true };

    const data = (await response.json().catch(() => ({}))) as {
      error?: AuthErrorCode;
      retryAfter?: number;
    };
    return { ok: false, error: data.error ?? 'network', retryAfter: data.retryAfter };
  } catch {
    // Rede caiu, offline, servidor fora: não é erro de credencial, e dizer
    // "email ou senha incorretos" aqui mandaria a pessoa procurar o problema
    // no lugar errado.
    return { ok: false, error: 'network' };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await fetch('/api/auth/me', { signal });
      const data = (await response.json()) as { user: AuthUser | null };
      setUser(response.ok ? data.user : null);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // A consulta inicial da sessão. Escrita como IIFE assíncrona com
  // AbortController em vez de chamar `refresh` direto: assim o estado só muda
  // depois do await — nunca de forma síncrona no corpo do efeito — e sair da
  // página no meio da requisição cancela em vez de atualizar componente morto.
  useEffect(() => {
    const controller = new AbortController();
    void (async () => {
      await refresh(controller.signal);
    })();
    return () => controller.abort();
  }, [refresh]);

  const signup = useCallback(
    async (input: SignupInput) => {
      const result = await post('/api/auth/signup', input);
      if (result.ok) await refresh();
      return result;
    },
    [refresh],
  );

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await post('/api/auth/login', { email, password });
      if (result.ok) await refresh();
      return result;
    },
    [refresh],
  );

  const logout = useCallback(async () => {
    await fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({ user, loading, signup, login, logout, refresh }),
    [user, loading, signup, login, logout, refresh],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  return ctx;
}
